import { Sim } from "../sim/sim";
import { Camera } from "../render/camera";
import type { Command } from "../sim/commands";
import { OWNER_PLAYER, KIND_UNIT, KIND_BUILDING, KIND_WRECKAGE, KIND_FEATURE } from "../sim/constants";
import { canPlace, placePos } from "../sim/placement";
import { audio } from "../render/audio";
import { clamp } from "../sim/math";
import { HEIGHT_SCALE } from "../render/pc/scene";

// Mouse + keyboard control (GDD §8.2): box select, shift-queue, control groups,
// double-click select-all-of-type, attack-move, build placement, strategic zoom.
// Selection / orders raycast against the 3D scene through the shared Camera
// (operator directive 2026-08-02: 100% PlayCanvas — no inverse-projection math).

export type OrderMarkerKind = "move" | "attack" | "reclaim" | "rally" | "repair";
export interface OrderMarker { x: number; y: number; kind: OrderMarkerKind; color: string }

// How the placement ghost is currently snapped (UI-only; drives ghost feedback).
// `magnetized` means it locked flush to a neighbour structure at (nx,ny).
export interface PlaceSnap { magnetized: boolean; nx: number; ny: number }

// Custom cursors (inline SVG data URIs — no external assets).
function cursor(svg: string, hx = 12, hy = 12): string {
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${hx} ${hy}, auto`;
}
const CUR_DEFAULT = "default";
const CUR_ATTACK = cursor(
  `<svg xmlns='http://www.w3.org/2000/svg' width='26' height='26'><g fill='none' stroke='%23ff5a3c' stroke-width='2'><circle cx='13' cy='13' r='8'/><path d='M13 1v5M13 20v5M1 13h5M20 13h5'/></g></svg>`, 13, 13);
const CUR_BUILD_OK = cursor(
  `<svg xmlns='http://www.w3.org/2000/svg' width='26' height='26'><g fill='none' stroke='%2378e6b0' stroke-width='2'><rect x='4' y='4' width='18' height='18' rx='2'/><path d='M13 8v10M8 13h10'/></g></svg>`, 13, 13);
const CUR_BUILD_BAD = cursor(
  `<svg xmlns='http://www.w3.org/2000/svg' width='26' height='26'><g fill='none' stroke='%23ff5a3c' stroke-width='2'><rect x='4' y='4' width='18' height='18' rx='2'/><path d='M8 8l10 10M18 8l-10 10'/></g></svg>`, 13, 13);
// Repair / resume-construction cursor: teal wrench-plus over a target ring.
const CUR_REPAIR = cursor(
  `<svg xmlns='http://www.w3.org/2000/svg' width='26' height='26'><g fill='none' stroke='%237cf0d0' stroke-width='2'><circle cx='13' cy='13' r='9'/><path d='M13 8v10M8 13h10'/></g></svg>`, 13, 13);

export class Input {
  selection = new Set<number>();
  hovered = -1;
  placing: { defId: string } | null = null;
  placeX = 0;
  placeY = 0;
  placeSnap: PlaceSnap | null = null; // current ghost snap state (UI feedback)
  private snapDisabled = false; // Alt held: temporarily disable placement snapping
  selBox: { x0: number; y0: number; x1: number; y1: number } | null = null;
  paused = false;
  pendingMarkers: OrderMarker[] = [];

  private pending: Command[] = [];
  private groups: number[][] = Array.from({ length: 10 }, () => []);
  private keys = new Set<string>();
  private canvas: HTMLCanvasElement;
  private sim: Sim;
  private cam: Camera;
  private dragStart: { sx: number; sy: number } | null = null;
  private dragging = false;
  private panDrag: { sx: number; sy: number } | null = null;
  // Armed order: the next left-click in the world issues this (from a hotkey or a
  // HUD command button). "" = none. "attack" = attack-move; "move"/"repair" issue
  // the same context-sensitive ground order right-click does (cursor differs).
  armed: "" | "move" | "attack" | "repair" = "";
  private lastClickTime = 0;
  private lastClickEnt = -1;
  private mouseSx = -1; // -1 = pointer has not moved over the canvas yet
  private mouseSy = -1;
  private mouseInside = false;
  onTogglePause?: () => void;

  constructor(canvas: HTMLCanvasElement, sim: Sim, cam: Camera) {
    this.canvas = canvas;
    this.sim = sim;
    this.cam = cam;
    this.bind();
  }

  drain(): Command[] {
    const c = this.pending;
    this.pending = [];
    return c;
  }

  /** Order markers issued since last drain (consumed by the renderer FX). */
  drainMarkers(): OrderMarker[] {
    const m = this.pendingMarkers;
    this.pendingMarkers = [];
    return m;
  }

  private mark(x: number, y: number, kind: OrderMarkerKind): void {
    const color =
      kind === "attack" ? "#ff5a3c" : kind === "reclaim" ? "#ffc24d" : kind === "repair" ? "#7cf0d0" : "#7ce6a0";
    this.pendingMarkers.push({ x, y, kind, color });
  }

  private issue(cmd: Command): void {
    this.pending.push(cmd);
  }

  /** Begin placing a building (from the build menu). */
  startPlacing(defId: string): void {
    this.placing = { defId };
  }

  /** Queue a unit at the selected factory (from the build menu). */
  queueUnit(defId: string, count: number): void {
    for (const e of this.selection) {
      if (this.sim.world.isAlive(e) && this.sim.world.def(e).role === "factory") {
        this.issue({ t: "factoryQueue", factory: e, defId, count });
      }
    }
  }

  /** Cancel one queued unit at the selected factory (build-menu right-click). */
  cancelUnit(defId: string): void {
    for (const e of this.selection) {
      if (this.sim.world.isAlive(e) && this.sim.world.def(e).role === "factory") {
        this.issue({ t: "factoryCancel", factory: e, defId });
      }
    }
  }

  // --- HUD command card: mouse-driven equivalents of the keyboard orders. Each
  //     arms/executes through the SAME command path the sim already handles, so
  //     the buttons and hotkeys are interchangeable and determinism is unaffected.

  /** Arm a context-sensitive ground order (next left-click issues it). */
  armMove(): void { if (this.hasMobileSelected()) this.armed = "move"; }
  /** Arm an attack-move (hotkey A). */
  armAttackMove(): void { if (this.hasMobileSelected()) this.armed = "attack"; }
  /** Arm a repair / resume-construction order (next click on a friendly site). */
  armRepair(): void { if (this.hasBuilderSelected()) this.armed = "repair"; }
  /** Stop all selected units (hotkey S). */
  stopSelected(): void {
    const sel = [...this.selection].filter((x) => this.sim.world.isAlive(x));
    if (sel.length) { this.issue({ t: "stop", ents: sel }); audio.ui("click"); }
  }
  /** Self-destruct selected (hotkey Del). */
  selfDestructSelected(): void {
    const sel = [...this.selection].filter((x) => this.sim.world.isAlive(x) && this.sim.world.owner[x] === OWNER_PLAYER);
    if (sel.length) { this.issue({ t: "selfDestruct", ents: sel }); audio.ui("deny"); }
  }
  /** Narrow the current selection to one unit type (selection-panel group click). */
  keepOnlyType(defId: string): void {
    const w = this.sim.world;
    const di = w.defIndex(defId);
    const keep = [...this.selection].filter((e) => w.isAlive(e) && w.defIdx[e] === di);
    if (keep.length) { this.selection = new Set(keep); audio.ui("select"); }
  }
  /** True if any selected friendly is a mobile unit (can move / attack-move). */
  hasMobileSelected(): boolean {
    const w = this.sim.world;
    for (const e of this.selection) if (w.isAlive(e) && w.owner[e] === OWNER_PLAYER && w.def(e).kind === "unit" && w.def(e).speed > 0) return true;
    return false;
  }
  /** True if any selected friendly is a builder (can repair / resume / assist). */
  hasBuilderSelected(): boolean {
    const w = this.sim.world;
    for (const e of this.selection) if (w.isAlive(e) && w.owner[e] === OWNER_PLAYER && w.kind[e] === KIND_UNIT && w.def(e).buildPower > 0) return true;
    return false;
  }
  /** True if any selected friendly unit can be self-destructed. */
  hasOwnSelected(): boolean {
    const w = this.sim.world;
    for (const e of this.selection) if (w.isAlive(e) && w.owner[e] === OWNER_PLAYER) return true;
    return false;
  }

  /** Ground (sim x, y) under a screen pixel — via camera raycast to the plane. */
  private ground(sx: number, sy: number): { x: number; y: number } {
    return this.cam.groundAt(sx, sy);
  }

  private bind(): void {
    const c = this.canvas;
    c.addEventListener("contextmenu", (e) => e.preventDefault());
    c.addEventListener("mousedown", (e) => this.onMouseDown(e));
    window.addEventListener("mousemove", (e) => this.onMouseMove(e));
    window.addEventListener("mouseup", (e) => this.onMouseUp(e));
    c.addEventListener("wheel", (e) => this.onWheel(e), { passive: false });
    window.addEventListener("keydown", (e) => this.onKeyDown(e));
    window.addEventListener("keyup", (e) => {
      const k = e.key.toLowerCase();
      this.keys.delete(k);
      if (k === "alt") {
        this.snapDisabled = false;
        if (this.placing) this.updatePlacePos(this.mouseSx, this.mouseSy);
      }
    });
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    this.cam.zoomAt(factor, e.clientX, e.clientY);
  }

  private onMouseDown(e: MouseEvent): void {
    this.mouseSx = e.clientX;
    this.mouseSy = e.clientY;
    this.snapDisabled = e.altKey;
    if (this.placing) this.updatePlacePos(e.clientX, e.clientY); // re-snap with current Alt state
    if (e.button === 0) {
      // Left click.
      if (this.placing) {
        this.confirmPlacement(e.shiftKey);
        return;
      }
      if (this.armed) {
        this.issueGroundOrder(e.clientX, e.clientY, e.shiftKey, this.armed === "attack");
        this.armed = "";
        return;
      }
      this.dragStart = { sx: e.clientX, sy: e.clientY };
      this.dragging = false;
    } else if (e.button === 2) {
      // Right click -> context command.
      if (this.placing) {
        this.placing = null;
        return;
      }
      if (this.armed) {
        this.armed = "";
        return;
      }
      this.rightClick(e.clientX, e.clientY, e.shiftKey);
    } else if (e.button === 1) {
      this.panDrag = { sx: e.clientX, sy: e.clientY };
    }
  }

  private onMouseMove(e: MouseEvent): void {
    this.mouseSx = e.clientX;
    this.mouseSy = e.clientY;
    this.mouseInside = true;
    this.snapDisabled = e.altKey;
    if (this.placing) {
      this.updatePlacePos(e.clientX, e.clientY);
    }
    if (this.panDrag) {
      this.cam.pan(-(e.clientX - this.panDrag.sx), -(e.clientY - this.panDrag.sy));
      this.panDrag = { sx: e.clientX, sy: e.clientY };
    }
    if (this.dragStart) {
      const dx = e.clientX - this.dragStart.sx;
      const dy = e.clientY - this.dragStart.sy;
      if (this.dragging || dx * dx + dy * dy > 36) {
        this.dragging = true;
        this.selBox = { x0: this.dragStart.sx, y0: this.dragStart.sy, x1: e.clientX, y1: e.clientY };
      }
    }
    // Hover.
    this.hovered = this.pickEntity(e.clientX, e.clientY, false);
  }

  private onMouseUp(e: MouseEvent): void {
    if (e.button === 1) this.panDrag = null;
    if (e.button !== 0) return;
    if (!this.dragStart) return;
    if (this.dragging && this.selBox) {
      this.boxSelect(this.selBox, e.shiftKey);
    } else {
      this.clickSelect(e.clientX, e.clientY, e.shiftKey);
    }
    this.dragStart = null;
    this.dragging = false;
    this.selBox = null;
  }

  private updatePlacePos(sx: number, sy: number): void {
    const g = this.ground(sx, sy);
    const rawX = g.x;
    const rawY = g.y;
    if (!this.placing || this.snapDisabled) {
      this.placeX = rawX;
      this.placeY = rawY;
      this.placeSnap = null;
      return;
    }
    const s = this.computeSnap(this.placing.defId, rawX, rawY);
    this.placeX = s.x;
    this.placeY = s.y;
    this.placeSnap = { magnetized: s.magnetized, nx: s.nx, ny: s.ny };
  }

  /**
   * Snap a proposed placement (UI-side only — the sim never sees the raw point).
   * Priority: extractors go to their metal spot (mirrors the sim); otherwise
   * MAGNETIZE flush to the nearest friendly structure's edge with a 1-cell gap
   * (so rows of solars / turret lines align), and if nothing is near, snap to the
   * map half-cell grid. Determinism is unaffected: the resulting coordinates are
   * baked into the build Command before it enters the sim.
   */
  private computeSnap(defId: string, rawX: number, rawY: number): { x: number; y: number; magnetized: boolean; nx: number; ny: number } {
    const w = this.sim.world;
    const def = w.def(w.defIndex(defId));
    // Extractors always bind to a metal spot in the sim; show the ghost there too.
    if (def.role === "extractor") {
      const p = placePos(w, defId, rawX, rawY);
      return { x: p.x, y: p.y, magnetized: false, nx: 0, ny: 0 };
    }
    const cs = w.gmap.cellSize;
    const gap = cs; // consistent 1-cell gap between flush structures
    const magRange = cs * 3; // magnetize when within ~3 cells of a structure edge
    // 1) Magnetize to the nearest friendly structure edge (built OR under construction).
    let best = -1;
    let bestEdge = magRange;
    for (let e = 0; e < w.count; e++) {
      if (!w.alive[e] || w.kind[e] !== KIND_BUILDING || w.owner[e] !== OWNER_PLAYER) continue;
      const edge = Math.hypot(w.posX[e] - rawX, w.posY[e] - rawY) - w.def(e).radius - def.radius;
      if (edge < bestEdge) {
        bestEdge = edge;
        best = e;
      }
    }
    if (best >= 0) {
      const nx = w.posX[best];
      const ny = w.posY[best];
      const rN = w.def(best).radius;
      const rG = def.radius;
      const dx = rawX - nx;
      const dy = rawY - ny;
      // Nearest edge wins: snap along the dominant axis, align on the other.
      if (Math.abs(dx) >= Math.abs(dy)) {
        const s = dx >= 0 ? 1 : -1;
        return { x: nx + s * (rN + gap + rG), y: ny, magnetized: true, nx, ny };
      }
      const s = dy >= 0 ? 1 : -1;
      return { x: nx, y: ny + s * (rN + gap + rG), magnetized: true, nx, ny };
    }
    // 2) Nothing near: snap to the nearest half-cell boundary.
    const half = cs / 2;
    return { x: Math.round(rawX / half) * half, y: Math.round(rawY / half) * half, magnetized: false, nx: 0, ny: 0 };
  }

  private confirmPlacement(shift: boolean): void {
    if (!this.placing) return;
    const defId = this.placing.defId;
    const builders = [...this.selection].filter(
      (e) => this.sim.world.isAlive(e) && this.sim.world.def(e).buildPower > 0 && this.sim.world.kind[e] === KIND_UNIT,
    );
    if (builders.length === 0) {
      this.placing = null;
      return;
    }
    if (canPlace(this.sim.world, defId, this.placeX, this.placeY)) {
      this.issue({ t: "build", ents: builders, defId, x: this.placeX, y: this.placeY, queue: shift });
      this.mark(this.placeX, this.placeY, "move");
      audio.ui("place");
    } else {
      audio.ui("deny");
    }
    if (!shift) this.placing = null;
  }

  private rightClick(sx: number, sy: number, shift: boolean): void {
    const ent = this.pickEntity(sx, sy, false);
    const sel = [...this.selection].filter((e) => this.sim.world.isAlive(e) && this.sim.world.owner[e] === OWNER_PLAYER);
    if (sel.length === 0) return;
    const w = this.sim.world;
    // If a factory is selected, right-click sets its rally point.
    const factories = sel.filter((e) => w.def(e).role === "factory");
    const mobiles = sel.filter((e) => w.def(e).kind === "unit" && w.def(e).speed > 0);
    const builders = mobiles.filter((e) => w.def(e).buildPower > 0 && e !== ent);
    if (ent >= 0 && w.owner[ent] === 2) {
      this.issue({ t: "attack", ents: mobiles, target: ent, queue: shift });
      this.mark(w.posX[ent], w.posY[ent], "attack");
      audio.ui("click");
    } else if (ent >= 0 && this.isRepairTarget(ent) && builders.length > 0) {
      // Friendly under-construction (resume) or damaged (repair) target: send builders.
      this.issue({ t: "repair", ents: builders, target: ent, queue: shift });
      this.mark(w.posX[ent], w.posY[ent], "repair");
      audio.ui("click");
    } else if (ent >= 0 && (w.kind[ent] === KIND_WRECKAGE || w.kind[ent] === KIND_FEATURE)) {
      const cons = mobiles.filter((e) => w.def(e).buildPower > 0);
      if (cons.length > 0) { this.issue({ t: "reclaim", ents: cons, target: ent, queue: shift }); this.mark(w.posX[ent], w.posY[ent], "reclaim"); }
      else { const g = this.ground(sx, sy); this.issue({ t: "move", ents: mobiles, x: g.x, y: g.y, queue: shift, attackMove: false }); }
      audio.ui("click");
    } else {
      const g = this.ground(sx, sy); const wx = g.x, wy = g.y;
      if (mobiles.length > 0) { this.issue({ t: "move", ents: mobiles, x: wx, y: wy, queue: shift, attackMove: false }); this.mark(wx, wy, "move"); }
      for (const f of factories) this.issue({ t: "factoryRally", factory: f, x: wx, y: wy });
      if (factories.length > 0 && mobiles.length === 0) this.mark(wx, wy, "rally");
      if (mobiles.length > 0 || factories.length > 0) audio.ui("click");
    }
  }

  private issueGroundOrder(sx: number, sy: number, shift: boolean, attackMove: boolean): void {
    const w = this.sim.world;
    const ent = this.pickEntity(sx, sy, false);
    const mobiles = [...this.selection].filter(
      (e) => w.isAlive(e) && w.owner[e] === OWNER_PLAYER && w.def(e).kind === "unit" && w.def(e).speed > 0,
    );
    if (mobiles.length === 0) return;
    const builders = mobiles.filter((e) => w.def(e).buildPower > 0 && e !== ent);
    if (ent >= 0 && w.owner[ent] === 2) {
      this.issue({ t: "attack", ents: mobiles, target: ent, queue: shift });
      this.mark(w.posX[ent], w.posY[ent], "attack");
    } else if (ent >= 0 && this.isRepairTarget(ent) && builders.length > 0) {
      this.issue({ t: "repair", ents: builders, target: ent, queue: shift });
      this.mark(w.posX[ent], w.posY[ent], "repair");
    } else {
      const g = this.ground(sx, sy); const wx = g.x, wy = g.y;
      this.issue({ t: "move", ents: mobiles, x: wx, y: wy, queue: shift, attackMove });
      this.mark(wx, wy, attackMove ? "attack" : "move");
    }
    audio.ui("click");
  }

  /** Is any selected unit (other than `exclude`) a builder that could repair/resume? */
  private hasSelectedBuilder(exclude: number): boolean {
    const w = this.sim.world;
    for (const e of this.selection) {
      if (e !== exclude && w.isAlive(e) && w.kind[e] === KIND_UNIT && w.def(e).buildPower > 0) return true;
    }
    return false;
  }

  /** A friendly unit/building that selected builders could resume-build or repair. */
  private isRepairTarget(ent: number): boolean {
    const w = this.sim.world;
    if (ent < 0 || !w.isAlive(ent) || w.owner[ent] !== OWNER_PLAYER) return false;
    if (w.kind[ent] !== KIND_UNIT && w.kind[ent] !== KIND_BUILDING) return false;
    return w.underConstruction(ent) || w.health[ent] < w.maxHealth[ent] - 0.01;
  }

  private clickSelect(sx: number, sy: number, shift: boolean): void {
    const ent = this.pickEntity(sx, sy, true);
    const now = performance.now();
    const isDouble = ent >= 0 && ent === this.lastClickEnt && now - this.lastClickTime < 320;
    this.lastClickTime = now;
    this.lastClickEnt = ent;

    if (ent < 0) {
      if (!shift) this.selection.clear();
      return;
    }
    if (isDouble) {
      // Select all of same type on screen.
      this.selectAllOfType(ent, shift);
      return;
    }
    if (!shift) this.selection.clear();
    if (this.selection.has(ent)) this.selection.delete(ent);
    else this.selection.add(ent);
    if (this.sim.world.owner[ent] === OWNER_PLAYER) audio.ui("select");
  }

  private selectAllOfType(proto: number, shift: boolean): void {
    const w = this.sim.world;
    const di = w.defIdx[proto];
    if (!shift) this.selection.clear();
    const v = this.cam.viewRect();
    for (let e = 0; e < w.count; e++) {
      if (!w.alive[e] || w.owner[e] !== OWNER_PLAYER || w.defIdx[e] !== di) continue;
      if (w.posX[e] < v.x0 || w.posX[e] > v.x1 || w.posY[e] < v.y0 || w.posY[e] > v.y1) continue;
      this.selection.add(e);
    }
  }

  private boxSelect(box: { x0: number; y0: number; x1: number; y1: number }, shift: boolean): void {
    const w = this.sim.world;
    const x0 = Math.min(box.x0, box.x1), x1 = Math.max(box.x0, box.x1);
    const y0 = Math.min(box.y0, box.y1), y1 = Math.max(box.y0, box.y1);
    if (!shift) this.selection.clear();
    const units: number[] = [];
    const buildings: number[] = [];
    for (let e = 0; e < w.count; e++) {
      if (!w.alive[e] || w.owner[e] !== OWNER_PLAYER) continue;
      const p = this.cam.project(w.posX[e], w.posY[e]);
      if (p.behind) continue;
      const sx = p.x, sy = p.y;
      if (sx < x0 || sx > x1 || sy < y0 || sy > y1) continue;
      if (w.kind[e] === KIND_UNIT) units.push(e);
      else if (w.kind[e] === KIND_BUILDING) buildings.push(e);
    }
    // Prefer units; only select buildings if no units were boxed.
    const pick = units.length > 0 ? units : buildings;
    for (const e of pick) this.selection.add(e);
  }

  /**
   * Pick the entity under a screen point by RAYCASTING the 3D scene: cast a world
   * ray from the pixel (Camera.screenRay) and test it against each entity's
   * vertical pick-capsule (a segment from its ground foot up through its drawn
   * body height, given a forgiving radius). Nearest hit along the ray wins — so
   * clicking a building's roof lands on the building, and a unit standing in
   * front of it beats the building behind. WYSIWYG at every zoom / tilt.
   */
  private pickEntity(sx: number, sy: number, playerOnly: boolean): number {
    const w = this.sim.world;
    const ray = this.cam.screenRay(sx, sy);
    let best = -1;
    let bestScore = Infinity;
    for (let e = 0; e < w.count; e++) {
      if (!w.alive[e]) continue;
      if (playerOnly && (w.owner[e] !== OWNER_PLAYER || w.kind[e] === KIND_FEATURE)) continue;
      const def = w.def(e);
      const kind = w.kind[e];
      const gx = w.posX[e], gz = w.posY[e];
      const gy = w.gmap.elevationAt(gx, gz) * HEIGHT_SCALE;
      let bodyH: number, pickR: number;
      if (kind === KIND_BUILDING) { bodyH = def.radius * 2.2; pickR = def.radius * 1.35 + 2; }
      else if (kind === KIND_UNIT) { bodyH = def.radius * 1.6; pickR = def.radius * 1.1 + 2; }
      else if (kind === KIND_FEATURE) { bodyH = def.radius * 2.0; pickR = def.radius * 0.9 + 2; }
      else { bodyH = def.radius * 0.8; pickR = def.radius * 0.9 + 2; } // wreckage
      const t = rayCapsuleT(ray, gx, gy, gy + bodyH, gz, pickR);
      if (t < 0) continue;
      // Nearest along the ray wins; a small priority bias lets an overlapping
      // unit beat the building behind it when capsules tie.
      const prio = kind === KIND_UNIT ? 0 : kind === KIND_BUILDING ? 1 : 2;
      const score = t + prio * 6;
      if (score < bestScore) { bestScore = score; best = e; }
    }
    return best;
  }

  private onKeyDown(ke: KeyboardEvent): void {
    const k = ke.key.toLowerCase();
    this.keys.add(k);
    if (k === "alt") {
      this.snapDisabled = true;
      if (this.placing) {
        this.updatePlacePos(this.mouseSx, this.mouseSy);
        ke.preventDefault(); // don't let Alt yank focus to the browser menu mid-placement
      }
      return;
    }
    // Control groups.
    if (/^[0-9]$/.test(k)) {
      const n = parseInt(k, 10);
      if (ke.ctrlKey || ke.metaKey) {
        this.groups[n] = [...this.selection];
      } else {
        const alive = this.groups[n].filter((x) => this.sim.world.isAlive(x));
        this.groups[n] = alive;
        this.selection = new Set(alive);
      }
      return;
    }
    if (k === "a") {
      this.armAttackMove();
    } else if (k === "s") {
      this.stopSelected();
    } else if (k === "escape") {
      this.placing = null;
      this.armed = "";
    } else if (k === "delete" || k === "backspace") {
      const sel = [...this.selection].filter((x) => this.sim.world.isAlive(x) && this.sim.world.owner[x] === OWNER_PLAYER);
      if (sel.length) this.issue({ t: "selfDestruct", ents: sel });
    } else if (k === " ") {
      this.paused = !this.paused;
      this.onTogglePause?.();
      ke.preventDefault();
    }
  }

  /** Camera pan from held keys / screen edges. Called each render frame. */
  updateCamera(dt: number): void {
    const speed = 500 / this.cam.zoom;
    let dx = 0, dy = 0;
    // Arrow keys pan (W/A/S/D are reserved for attack-move/stop; edge-pan + drag also work).
    if (this.keys.has("arrowleft")) dx -= 1;
    if (this.keys.has("arrowright")) dx += 1;
    if (this.keys.has("arrowup")) dy -= 1;
    if (this.keys.has("arrowdown")) dy += 1;
    // Edge panning (only once the pointer has actually been over the canvas,
    // so the default (offscreen) position never drifts the camera).
    const edge = 6;
    if (this.mouseInside && this.mouseSx >= 0) {
      if (this.mouseSx <= edge) dx -= 1;
      else if (this.mouseSx >= this.cam.screenW - edge) dx += 1;
      if (this.mouseSy <= edge) dy -= 1;
      else if (this.mouseSy >= this.cam.screenH - edge) dy += 1;
    }
    if (dx !== 0 || dy !== 0) {
      const m = Math.hypot(dx, dy) || 1;
      this.cam.panWorld((dx / m) * speed * dt, (dy / m) * speed * dt);
    }
  }

  /** Recompute hover / placement each frame in world terms. */
  refresh(): void {
    if (this.placing) this.updatePlacePos(this.mouseSx, this.mouseSy);
    // Prune dead selection.
    for (const e of this.selection) if (!this.sim.world.isAlive(e)) this.selection.delete(e);
    this.hovered = this.pickEntity(this.mouseSx, this.mouseSy, false);
    this.updateCursor();
  }

  private updateCursor(): void {
    let c = CUR_DEFAULT;
    if (this.placing) {
      c = canPlace(this.sim.world, this.placing.defId, this.placeX, this.placeY) ? CUR_BUILD_OK : CUR_BUILD_BAD;
    } else if (this.armed === "attack") {
      c = CUR_ATTACK;
    } else if (this.armed === "repair") {
      c = CUR_REPAIR;
    } else if (this.hovered >= 0 && this.sim.world.isAlive(this.hovered) && this.sim.world.owner[this.hovered] === 2 && this.selection.size > 0) {
      c = CUR_ATTACK;
    } else if (this.hovered >= 0 && this.isRepairTarget(this.hovered) && this.hasSelectedBuilder(this.hovered)) {
      c = CUR_REPAIR;
    }
    if (this.canvas.style.cursor !== c) this.canvas.style.cursor = c;
  }

  focusSelection(): void {
    // 'F' style — center camera on selection (used by minimap etc.).
    const w = this.sim.world;
    let sx = 0, sy = 0, n = 0;
    for (const e of this.selection) {
      if (w.isAlive(e)) { sx += w.posX[e]; sy += w.posY[e]; n++; }
    }
    if (n) this.cam.centerOn(sx / n, sy / n);
  }
}

// Closest-approach ray parameter against a vertical pick-capsule (axis
// (ax, ay0..ay1, az), radius R). Returns t >= 0 if within R, else -1. Because the
// axis is vertical we solve the horizontal closest approach then clamp the
// segment in Y — exact enough for forgiving RTS selection and cheap enough for a
// full per-entity scan per pick.
function rayCapsuleT(
  ray: { ox: number; oy: number; oz: number; dx: number; dy: number; dz: number },
  ax: number, ay0: number, ay1: number, az: number, R: number,
): number {
  const { ox, oy, oz, dx, dy, dz } = ray;
  const denom = dx * dx + dz * dz;
  let t: number;
  if (denom > 1e-6) t = -((ox - ax) * dx + (oz - az) * dz) / denom;
  else t = Math.abs(dy) > 1e-6 ? ((ay0 + ay1) * 0.5 - oy) / dy : 0;
  if (t < 0) t = 0;
  const rx = ox + t * dx, ry = oy + t * dy, rz = oz + t * dz;
  const cy = clamp(ry, ay0, ay1);
  const ex = rx - ax, ey = ry - cy, ez = rz - az;
  return ex * ex + ey * ey + ez * ez <= R * R ? t : -1;
}
